
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, 
  Key, 
  Database, 
  CheckCircle, 
  XCircle, 
  Copy, 
  ExternalLink,
  Code,
  FileText,
  Wrench
} from 'lucide-react';
import type { CustomAction } from '@/types/action';

interface ActionApiConfigTabProps {
  action: CustomAction;
}

export const ActionApiConfigTab = ({ action }: ActionApiConfigTabProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Mock data for demonstration - in real app this would come from action.lastTestResult
  const lastTestResult = action.lastTestResult || {
    success: true,
    response: {
      status: 200,
      data: {
        user: {
          id: "12345",
          name: "John Doe",
          email: "john@example.com",
          created_at: "2024-01-15T10:30:00Z"
        },
        metadata: {
          timestamp: "2024-01-15T10:30:00Z",
          version: "1.0"
        }
      }
    }
  };

  // Extract available output fields from the last test result
  const extractOutputFields = (data: any, prefix = ''): string[] => {
    if (!data || typeof data !== 'object') return [];
    
    const fields: string[] = [];
    for (const [key, value] of Object.entries(data)) {
      const fieldPath = prefix ? `${prefix}.${key}` : key;
      fields.push(fieldPath);
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        fields.push(...extractOutputFields(value, fieldPath));
      }
    }
    return fields;
  };

  const outputFields = extractOutputFields(lastTestResult.response?.data);

  // Hero stats
  const stats = [
    {
      label: 'API Endpoint',
      value: action.url || 'Not configured',
      icon: ExternalLink,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'HTTP Method',
      value: action.method || 'GET',
      icon: Code,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      label: 'Parameters',
      value: action.parameters?.length || 0,
      icon: Settings,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Output Fields',
      value: outputFields.length,
      icon: Database,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${stat.bgColor}`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* API Configuration Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Configuration */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Settings className="h-5 w-5 text-blue-600" />
              Basic Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Endpoint URL</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-mono">
                  {action.url || 'Not configured'}
                </code>
                {action.url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyToClipboard(action.url!, 'url')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">HTTP Method</label>
              <div className="mt-1">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {action.method || 'GET'}
                </Badge>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Content Type</label>
              <div className="mt-1">
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                  application/json
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Key className="h-5 w-5 text-green-600" />
              Authentication
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {action.headers ? (
              <div>
                <label className="text-sm font-medium text-gray-700">Headers</label>
                <pre className="mt-1 p-3 bg-gray-100 rounded-md text-sm font-mono overflow-x-auto">
                  {JSON.stringify(JSON.parse(action.headers), null, 2)}
                </pre>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Key className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>No authentication headers configured</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Parameters */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Wrench className="h-5 w-5 text-purple-600" />
            Parameters
            <Badge variant="outline" className="ml-2 text-xs">
              {action.parameters?.length || 0} parameters
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {action.parameters && action.parameters.length > 0 ? (
            <div className="space-y-3">
              {action.parameters.map((param, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white/60 rounded-lg border border-white/60 backdrop-blur-sm">
                  <div>
                    <p className="font-medium text-gray-900">{param}</p>
                    <p className="text-sm text-gray-600">
                      {action.parameterDescriptions ? 
                        JSON.parse(action.parameterDescriptions)[param] || 'No description' 
                        : 'No description'}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    Required
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Wrench className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No parameters configured</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Example Response */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <FileText className="h-5 w-5 text-orange-600" />
            Example API Response
            {lastTestResult.success ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle className="h-3 w-3 mr-1" />
                Last Test: Success
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <XCircle className="h-3 w-3 mr-1" />
                Last Test: Failed
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lastTestResult.response?.data ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Response from last successful test</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyToClipboard(JSON.stringify(lastTestResult.response.data, null, 2), 'response')}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copiedField === 'response' ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <pre className="p-4 bg-gray-100 rounded-lg text-sm font-mono overflow-x-auto max-h-96">
                {JSON.stringify(lastTestResult.response.data, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No response data available</p>
              <p className="text-sm">Run a test to see example response data</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Output Fields */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Database className="h-5 w-5 text-blue-600" />
            Available Output Fields
            <Badge variant="outline" className="ml-2 text-xs">
              {outputFields.length} fields
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {outputFields.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                These fields can be used in workflows and automations based on the API response structure:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {outputFields.map((field, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-white/60 rounded border border-white/60 backdrop-blur-sm hover:bg-white/80 transition-colors">
                    <code className="text-sm font-mono text-gray-700">{field}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyToClipboard(field, field)}
                      className="h-6 w-6 p-0"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Database className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No output fields detected</p>
              <p className="text-sm">Run a successful test to extract available fields</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
